// PostCardCategory.jsx
import React, { useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PostCard from '../container/PostCard.js';

import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';

import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getTranslationKey from '../resource/getTranslationKey.js';

import media from '../style/media.js';
import TextEllipsis from '../style/TextEllipsis.js';

import MoreIconSource from '../../img/ic-arrow-right-xs.svg';

const PostCardPlaceholder = () => {
  const result = useMemo(() => {
    return (
      <GridList>
        {new Array(9).fill(null).map((id, index) => (
          <PostCardWrapper key={index}>
            <PostCardLink>
              <PostCard />
            </PostCardLink>
          </PostCardWrapper>
        ))}
      </GridList>
    );
  }, []);
  return result;
};

export const PostCardCategory = ({
  category,
  titleI18nId,
  moreLink,
  titleLink,
  nextPage,
  index,
  isNextPageFetching,
  isFirstPageFetched,
  shouldRefresh,
  shouldShowMoreLink,
  isProfileCategory,
  isTitleClickable,
  messageIds,
  fetchFeeds,
}) => {
  const { t } = useTranslation();
  const fetchData = useCallback(
    ({ page = 1, shouldSkipLocalCache }) => {
      if (category) {
        fetchFeeds({
          type: category,
          page,
          shouldSkipLocalCache,
        });
      }
    },
    [category, fetchFeeds]
  );
  const trackClick = useCallback(({ buttonId, messageId, extraProps }) => {
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({
          pathname: window.location.pathname,
        }),
        buttonId,
        messageId,
        extraProps,
      },
    });
  }, []);
  useEffect(() => {
    if (shouldRefresh) {
      fetchData({ page: 1, shouldSkipLocalCache: true });
    }
  }, [fetchData, shouldRefresh]);
  useEffect(() => {
    fetchData({ page: 1, shouldSkipLocalCache: true });
  }, [fetchData]);
  if (isFirstPageFetched && !messageIds.length) {
    return null;
  }
  return (
    <StyledPostCardCategory>
      <Header>
        <Title
          to={titleLink}
          as={isTitleClickable ? Link : undefined}
          replace={isProfileCategory}
        >
          {t(titleI18nId || getTranslationKey({ key: category }), {
            ns: TranslationNamespace.FEED,
          })}
        </Title>
        {shouldShowMoreLink && (
          <MoreLink
            to={moreLink}
            onClick={() => {
              trackClick({
                buttonId: ButtonId.All.ButtonMore,
                extraProps: {
                  'discover.index': index,
                  'discover.category': category,
                },
              });
            }}
          >
            {t('more', {
              ns: TranslationNamespace.GENERAL,
            })}
            <img src={MoreIconSource} aria-hidden />
          </MoreLink>
        )}
      </Header>
      <Body>
        {!isFirstPageFetched ? (
          <PostCardPlaceholder />
        ) : (
          <InfiniteScroller
            axis={'y'}
            useWindow={true}
            pageStart={1}
            hasMore={nextPage != null}
            loadMore={() => {
              fetchData({ page: nextPage });
            }}
            isFetching={isNextPageFetching}
            threshold={200}
          >
            <GridList>
              <ScrollItem loader={<PostCardWrapper />}>
                {messageIds
                  .filter(id => id)
                  .map(messageId => {
                    return (
                      <PostCardWrapper key={messageId}>
                        <PostCardLink
                          as={Link}
                          to={`/post/${messageId}`}
                          onClick={() => {
                            trackClick({
                              buttonId: ButtonId.All.ButtonPostCard,
                              messageId,
                              extraProps: {
                                'discover.index': index,
                                'discover.category': category,
                              },
                            });
                          }}
                        >
                          <PostCard postId={messageId} />
                        </PostCardLink>
                      </PostCardWrapper>
                    );
                  })}
              </ScrollItem>
            </GridList>
          </InfiniteScroller>
        )}
      </Body>
    </StyledPostCardCategory>
  );
};

PostCardCategory.propTypes = {
  category: PropTypes.string,
  titleI18nId: PropTypes.string,
  moreLink: PropTypes.string,
  titleLink: PropTypes.string,
  index: PropTypes.number,
  nextPage: PropTypes.number,
  isNextPageFetching: PropTypes.bool,
  isFirstPageFetched: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
  shouldShowMoreLink: PropTypes.bool,
  isProfileCategory: PropTypes.bool,
  isTitleClickable: PropTypes.bool,
  messageIds: PropTypes.array,
  fetchFeeds: PropTypes.func,
};

PostCardCategory.defaultProps = {
  category: '',
  titleI18nId: '',
  moreLink: '',
  titleLink: '',
  index: null,
  nextPage: null,
  isNextPageFetching: false,
  isFirstPageFetched: false,
  shouldRefresh: false,
  shouldShowMoreLink: false,
  isProfileCategory: false,
  isTitleClickable: false,
  messageIds: [],
  fetchFeeds: () => null,
};

const StyledPostCardCategory = styled.div``;

const Header = styled.header`
  margin-bottom: 12px;
  padding-left: 16px;
  padding-right: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  ${TextEllipsis};
  margin-right: 10px;
  flex: auto;
  display: block;
  font-size: 20px;
  font-weight: 600;
  line-height: 150%;
  ${media.mobile`
    font-size: 18px;
  `}
`;

const MoreLink = styled(Link)`
  flex: none;
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  ${media.mobile`
    font-size: 12px;
  `}
  >img {
    margin-left: 4px;
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

const Body = styled.main``;

const GridList = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 4px;
  ${media.mobile`
    padding-left: 2px;
    padding-right: 2px;
    grid-gap: 2px;
  `};
`;

const PostCardWrapper = styled.div`
  position: relative;
  padding-top: 100%;
`;

const PostCardLink = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
`;

export default PostCardCategory;
